import { ReactNode, RefObject } from "react";

import styles from "./RailDock.module.css";
import { ResizablePanel } from "./ResizablePanel";

interface RailDockProps {
  /** Always-visible rail content (the vertical activity bar). */
  rail: ReactNode;
  /** The open panel docked against the rail, or null when no panel is open. */
  panel?: ReactNode;
  /** The outer scroll container the panel and rail stick within. */
  scrollRef: RefObject<HTMLElement | null>;
  /** Height of scrollRef's visible area, when the parent already measures
      it. Omit to let the panel measure the element itself. */
  scrollerHeight?: number;
  /** Sticky offset below the toolbar. */
  offsetTop?: number;
  /** Optional ref to the panel's scroll element (e.g. for wheel forwarding). */
  panelScrollRef?: RefObject<HTMLDivElement | null>;
  /** Fixed rail width in px. Defaults to 44. */
  railWidth?: number;
  /** Controlled panel width (px). Pair with onPanelWidthChange so dragging
      still applies; omit both for panel-local width (360 default). */
  panelWidth?: number;
  onPanelWidthChange?: (width: number) => void;
  panelMinWidth?: number;
  panelMaxWidth?: number;
  /** aria-label root for the panel region. */
  label?: string;
}

/**
 * An always-visible vertical rail with an optional resizable panel docked to
 * its left, separated from the content by a 1px divider. Renders as a
 * fragment so it drops into a flex-row layout beside the content it docks
 * against (same contract as <ResizablePanel>).
 */
export const RailDock = ({
  rail,
  panel,
  scrollRef,
  scrollerHeight,
  offsetTop = 0,
  panelScrollRef,
  railWidth = 44,
  panelWidth,
  onPanelWidthChange,
  panelMinWidth,
  panelMaxWidth,
  label,
}: RailDockProps) => (
  <>
    {panel != null && (
      <ResizablePanel
        scrollRef={scrollRef}
        scrollerHeight={scrollerHeight}
        offsetTop={offsetTop}
        panelScrollRef={panelScrollRef}
        width={panelWidth}
        onWidthChange={onPanelWidthChange}
        minWidth={panelMinWidth}
        maxWidth={panelMaxWidth}
        label={label}
      >
        {panel}
      </ResizablePanel>
    )}
    <div className={styles.separator} />
    <div className={styles.rail} style={{ flex: `0 0 ${railWidth}px` }}>
      <div className={styles.railSticky} style={{ top: offsetTop }}>
        {rail}
      </div>
    </div>
  </>
);
